import { BOOLEAN_CHOICES } from 'constants/booleanChoices';
import { OperationAccess } from 'constants/operationAccesses';
import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import { GatewaysListFilterStatusSelectInput } from 'pages/Gateways/GatewaysList/GatewaysListFilter/GatewaysListFilterStatusSelectInput/GatewaysListFilterStatusSelectInput';
import { useGetList } from 'react-admin';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { AutocompleteInput, EnumAutocompleteInput, TextInput } from 'shared/react-admin/Inputs';
import { ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { GatewayStatus } from 'types/embedded/gateway';

const getBooleanChoiceName = (filterValue: boolean) =>
  BOOLEAN_CHOICES.find((value) => value.id === filterValue)?.name as string;

export const useGatewaysListFilterFields = (): FilterFieldConfig[] => {
  const { data: gatewayStatuses } = useGetList<GatewayStatus>(Resources.GatewayStatuses);
  const { hasOperationAccess } = usePermissions();

  return [
    {
      name: 'name',
      label: 'Поиск по названию',
      chipType: 'text',
      input: (
        <TextInput
          helperText={false}
          key="name"
          label="Поиск по названию"
          resettable
          source="name"
          variant="outlined"
        />
      ),
    },
    {
      name: 'status',
      label: 'Статус',
      chipType: 'text',
      chipProps: {
        getName: (filterValue: string) =>
          gatewayStatuses?.find((status) => status?.id === filterValue)?.description || '',
      },
      input: <GatewaysListFilterStatusSelectInput />,
    },
    ...(hasOperationAccess(OperationAccess.ManageMerchantAccounts)
      ? [
          {
            name: 'managerVerified',
            label: 'Проверен менеджером',
            chipType: 'text' as const,
            chipProps: {
              getName: getBooleanChoiceName,
              isNotIgnoreFalseValue: true,
            },
            input: (
              <AutocompleteInput
                choices={BOOLEAN_CHOICES}
                fullWidth
                helperText={false}
                label="Проверен менеджером"
                name="managerVerified"
                source="managerVerified"
                variant="outlined"
              />
            ),
          },
        ]
      : []),
    {
      name: 'provider',
      label: 'Провайдер',
      chipType: 'autocomplete',
      chipProps: {
        resource: Resources.Providers,
      },
      input: (
        <ReferenceInput
          name="provider"
          perPage={100}
          reference={Resources.Providers}
          sort={{ field: 'name', order: 'ASC' }}
          source="provider"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Провайдер"
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'alias',
      label: 'Алиас',
      chipType: 'text',
      input: (
        <TextInput
          helperText={false}
          key="alias"
          label="Алиас"
          resettable
          source="alias"
          variant="outlined"
        />
      ),
    },
    {
      name: 'direction',
      label: 'Направление',
      chipType: 'enumAutocomplete',
      chipProps: {
        resource: Resources.Directions,
      },
      input: (
        <EnumAutocompleteInput
          fullWidth
          helperText={false}
          label="Направление"
          name="direction"
          optionText="description"
          optionValue="value"
          resource={Resources.Directions}
          source="direction"
          variant="outlined"
        />
      ),
    },
    {
      name: 'pcidss',
      label: 'PCI DSS',
      chipType: 'text',
      chipProps: {
        getName: getBooleanChoiceName,
        isNotIgnoreFalseValue: true,
      },
      input: (
        <AutocompleteInput
          choices={BOOLEAN_CHOICES}
          fullWidth
          helperText={false}
          label="PCI DSS"
          name="pcidss"
          source="pcidss"
          variant="outlined"
        />
      ),
    },
    {
      name: 'pushApi',
      label: 'Push API',
      chipType: 'text',
      chipProps: {
        getName: getBooleanChoiceName,
        isNotIgnoreFalseValue: true,
      },
      input: (
        <AutocompleteInput
          choices={BOOLEAN_CHOICES}
          fullWidth
          helperText={false}
          label="Push API"
          name="pushApi"
          source="pushApi"
          variant="outlined"
        />
      ),
    },
  ];
};
